import React from "react";
import Button from "../../../components/Button";
import { FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING } from "../../../components/Form/FormItems/FormItemResourceSelectLazyLoading";
import { FORM_ITEM_TEXT } from "../../../components/Form/FormItems/FormItemText";
import { FORM_ITEM_TINY_MCE } from "../../../components/Form/FormItems/FormItemTinyMCE";
import { trainingLanguages } from "./config";

/**
 * Builds the core identity and classification field descriptors for the training form.
 * Covers: Code, Area, Method, Tags, Branch scope, Name, Version, Description,
 * Training Label, Category, Duration, Language, Alternative Languages, Prerequisites.
 */
export const buildCoreFields = ({
  data,
  t,
  form,
  isNewRecord,
  branchesVisible,
  setBranchesVisible,
  language,
  setLanguage
}) => [
  {
    required: true,
    type: FORM_ITEM_TEXT,
    max: 200,
    name: "code",
    label: "trg.new_training.code",
    placeholder: "trg.new_training.code",
    fieldProps: {
      disabled: !!data.shared_by_company_name
    }
  },
  {
    required: true,
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "area_id",
    label: "trg.new_training.area",
    placeholder: "trg.new_training.area",
    apiRoute:
      "trg/areas?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1",
    fieldProps: {
      requestOnMount: !isNewRecord
    }
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "method_id",
    label: "qam.activity_program.method",
    placeholder: "qam.activity_program.method",
    apiRoute:
      "trg/methods?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1",
    fieldProps: {
      requestOnMount: !isNewRecord
    }
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "tags",
    mode: "multiple",
    label: "trg.new_training.tags",
    placeholder: "trg.new_training.tags",
    apiRoute: "trg/tags?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1",
    fieldProps: {
      disabled: !!data.shared_by_company_name,
      requestOnMount: !isNewRecord
    }
  },
  {
    required: true,
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "range_of_validity",
    mode: "multiple",
    label: "trg.new_training.range_of_validity",
    placeholder: "trg.new_training.range_of_validity",
    apiRoute: "organization-company-branches?order_by_column%5B%5D=name&order_by_type%5B%5D=asc",
    fieldProps: {
      requestOnMount: !isNewRecord,
      popupRender: (menu) => {
        return (
          <>
            <div className="ml-10 mb-10 mt-10">
              <Button
                size="small"
                type="secondary"
                onClick={() => {
                  setBranchesVisible(false);
                  form.setFieldsValue({
                    range_of_validity: [{ value: t("trg.new_training.all_branches"), all_branches: true }]
                  });
                }}
              >
                {t("trg.new_training.all_branches")}
              </Button>
            </div>
            {menu}
          </>
        );
      },
      onOpenChange: (visible) => {
        if (visible) {
          const validityValue = form.getFieldValue("range_of_validity");
          if (validityValue?.[0]?.all_branches) {
            return;
          }
        }
        setBranchesVisible(visible);
      },
      open: branchesVisible
    }
  },
  {
    required: true,
    type: FORM_ITEM_TEXT,
    max: 200,
    name: "name",
    label: "trg.new_training.name",
    placeholder: "trg.new_training.name",
    fieldProps: {
      disabled: !!data.shared_by_company_name
    }
  },
  {
    type: FORM_ITEM_TEXT,
    name: "version",
    label: "trg.new_training.version",
    placeholder: "trg.new_training.version",
    fieldProps: {
      disabled: !!data.shared_by_company_name
    }
  },
  {
    type: FORM_ITEM_TINY_MCE,
    name: "description",
    label: "trg.new_training.description",
    placeholder: "trg.new_training.description",
    fieldProps: {
      disabled: !!data.shared_by_company_name,
      "showMenuBar": false
    }
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "training_label_id",
    label: "trg.training_program.training_label",
    placeholder: "trg.training_program.training_label",
    apiRoute: "trg/training-labels?order_by_column%5B%5D=name&order_by_type%5B%5D=asc",
    fieldProps: {
      initialValue: data.label ? { text: data.label.name, value: data.label.id } : undefined,
      requestOnMount: !isNewRecord
    }
  },
  {
    required: true,
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "category",
    label: "trg.new_training.category",
    placeholder: "trg.new_training.category",
    fieldProps: {
      showSearch: false,
      disabled: !!data.shared_by_company_name,
      staticOptions: [
        { transKey: "trg.new_training.initial", value: "trg.new_training.initial" },
        { transKey: "trg.new_training.recurrent", value: "trg.new_training.recurrent" }
      ]
    }
  },
  {
    required: true,
    type: FORM_ITEM_TEXT,
    name: "duration",
    label: "trg.new_training.duration",
    placeholder: "trg.new_training.duration"
  },
  {
    required: true,
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "language",
    label: "trg.new_training.language",
    placeholder: "trg.new_training.language",
    fieldProps: {
      showSearch: false,
      disabled: !!data.shared_by_company_name,
      staticOptions: trainingLanguages,
      onChange: (value) => {
        setLanguage(value);
        const alternativeLanguages = form.getFieldValue("alternative_languages");
        if (value && alternativeLanguages && alternativeLanguages.length > 0) {
          form.setFieldsValue({
            alternative_languages: alternativeLanguages.filter((altLang) => altLang !== value)
          });
        }
      }
    }
  },
  {
    mode: "multiple",
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "alternative_languages",
    label: "trg.new_training.alternative_languages",
    placeholder: "trg.new_training.alternative_languages",
    fieldProps: {
      overwritePreviousOptions: true,
      disabled: !!data.shared_by_company_name,
      staticOptions: trainingLanguages.filter((item) => item.value !== language)
    }
  },
  {
    type: FORM_ITEM_RESOURCE_SELECT_LAZY_LOADING,
    name: "prerequisites",
    mode: "multiple",
    label: "trg.new_training.prerequisites",
    placeholder: "trg.new_training.prerequisites",
    apiRoute:
      "trg/trainings?field%5B%5D=active&op%5B%5D=eq&order_by_column%5B%5D=name&order_by_type%5B%5D=asc&term%5B%5D=1",
    fieldProps: {
      searchColumns: ["name", "code"],
      requestOnMount: !isNewRecord
    }
  }
];
